/**
 * Shared column header row for rack + repeatable instance tables (#995 / #1154).
 */

import React from 'react';
import { View, Text } from 'react-native';

import {
  recordTableStyles,
  type RecordTableColumnAlign,
  type RecordTableLayout,
  resolveRecordTableColumnAlign,
} from './recordTableStyles';
import { repeatableCompactActionsColumnReserved } from './repeatableRowChrome';

export interface RecordTableHeaderProps<C extends string = string> {
  columns: readonly C[];
  getColumnAlign: (column: C) => RecordTableColumnAlign | undefined;
  getHeaderLabel: (column: C) => string;
  getColumnCellStyle: (
    column: C,
    layout: RecordTableLayout,
  ) => Record<string, unknown>;
  reorderEnabled?: boolean;
  /** Show drag column chrome on web even when reorder is inactive (disabled handle). */
  showDragColumn?: boolean;
  tableLayout?: RecordTableLayout;
  /**
   * Read-only lists (e.g. Devices browse) have no overflow menu, so the trailing
   * actions column is dead space. Collapse it on native compact only — web
   * (desktop + mobile web) keeps the reserved spacer unchanged (#999).
   */
  readOnly?: boolean;
  /** A read-only row can still expose its non-mutating View action. */
  hasOpenAction?: boolean;
}

export function RecordTableHeader<C extends string = string>({
  columns,
  getColumnAlign,
  getHeaderLabel,
  getColumnCellStyle,
  reorderEnabled = false,
  showDragColumn = false,
  tableLayout = 'desktop',
  readOnly = false,
  hasOpenAction = false,
}: RecordTableHeaderProps<C>): React.ReactElement {
  const isCompact = tableLayout === 'compact';
  const showCompactActionsColumn = repeatableCompactActionsColumnReserved({
    isCompact,
    readOnly,
    hasOpenAction,
  });
  const showDragSpacer = reorderEnabled || showDragColumn;

  return (
    <View
      style={[
        recordTableStyles.headerRow,
        isCompact && recordTableStyles.headerRowCompact,
      ]}
      accessibilityElementsHidden
    >
      <View style={recordTableStyles.headerBarSpacer} />
      {showDragSpacer ? (
        <View style={recordTableStyles.headerDragSpacer} />
      ) : null}
      {columns.map(column => {
        const isCentered =
          resolveRecordTableColumnAlign(getColumnAlign(column)) === 'center';
        return (
          <View
            key={column}
            style={[
              isCompact
                ? recordTableStyles.compactHeaderCell
                : recordTableStyles.headerCell,
              isCentered
                ? recordTableStyles.headerCellCenter
                : recordTableStyles.headerCellStart,
              getColumnCellStyle(column, tableLayout),
            ]}
          >
            <Text
              style={[
                recordTableStyles.headerText,
                isCompact && recordTableStyles.headerTextCompact,
                isCentered && recordTableStyles.headerTextCenter,
              ]}
              numberOfLines={1}
              ellipsizeMode="tail"
            >
              {getHeaderLabel(column)}
            </Text>
          </View>
        );
      })}
      <View
        style={[
          recordTableStyles.headerActionsSpacer,
          isCompact &&
            (showCompactActionsColumn
              ? recordTableStyles.headerActionsSpacerCompact
              : recordTableStyles.headerActionsSpacerHidden),
        ]}
      />
    </View>
  );
}
